import { useCallback, useMemo, useRef, useState } from 'react';
import { Input, ValidationState } from '@salecycle/ui-component-library';

const MIN_CHARACTERS = 5;
const MAX_CHARACTERS = 100;

export interface CampaignNameInputProps extends IntrinsicProps {
  name: string;
  setName: (name: string) => void;
  placeholder?: string;
  validationMessage?: {
    warning?: string;
    success?: string;
  };
}

export const CampaignNameInput = ({
  name,
  setName,
  placeholder,
  validationMessage,
  ...props
}: CampaignNameInputProps) => {
  const [focused, setFocused] = useState(false);
  const [clickedAway, setClickedAway] = useState(false);
  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => {
    setFocused(false);
    setClickedAway(true);
  }, []);

  // We want to show the warning once a valid name has been entered but is then removed.
  // This is important to check as there is an instance where the user has not yet clicked away but they are still typing on their
  // first focus.
  //
  // Take the following scenario:
  // The input loads for the first time (no validation state registered), the user focuses the input, they enter a 5 character name,
  // they remove one of the characters > name is in an invalid state, so we show the warning.
  const originalValidName = useRef(name.length >= MIN_CHARACTERS && name.length <= MAX_CHARACTERS ? name : null);

  const validationState = useMemo((): { state: ValidationState; message: string } => {
    if ((originalValidName.current || clickedAway) && (name.length < MIN_CHARACTERS || name.length > MAX_CHARACTERS)) {
      return {
        state: 'warning',
        message: validationMessage?.warning,
      };
    }

    if (name.length >= MIN_CHARACTERS && name.length <= MAX_CHARACTERS) {
      originalValidName.current = name;

      return {
        state: 'complete',
        message: validationMessage?.success,
      };
    }

    return {
      state: null,
      message: null,
    };
  }, [clickedAway, name, validationMessage?.success, validationMessage?.warning]);

  return (
    <div>
      <Input
        ariaLabel={props['aria-label']}
        label="Campaign name (5 - 100 characters)"
        placeholder={placeholder}
        value={name}
        onChange={setName}
        onFocus={onFocus}
        onBlur={onBlur}
        focusMode={focused}
        charLength={focused && name?.length}
        validationState={validationState.state}
        validationMessage={validationState.message}
        spacingBelow={false}
      />
      <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
        {name?.length ?? 0}/{MAX_CHARACTERS}
      </div>
    </div>
  );
};
